import React, { useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  onAuthStateChanged 
} from 'firebase/auth';
import { useAuthStore, User } from '../store/authStore';
import { firebaseApp as app  } from '../services/firebase';

import './AuthGuard.css';

const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const isAuthorized = useAuthStore.use.isAuthorized();
  const loading = useAuthStore.use.loading();
  const setUser = useAuthStore.use.setUser();
  const setIsAuthorized = useAuthStore.use.setIsAuthorized();
  const setLoading = useAuthStore.use.setLoading();


  useEffect(() => {
    // Listen for authentication state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser && firebaseUser.email && firebaseUser.email?.endsWith('@sunshinestarts.com')) {
          const userData: User = {
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || '',
            id: firebaseUser.uid,
            photoURL: firebaseUser.photoURL || undefined
          };
          
          setUser(userData);
          setIsAuthorized(true);
        } else {
          // User is signed out
          setUser(null);
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error('Authentication error:', error);
        setUser(null);
        setIsAuthorized(false);
      } finally {
        setLoading(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [setUser, setIsAuthorized, setLoading]);

  const handleSignIn = async () => {
    try {
      setLoading(true);
      await signInWithPopup(auth, googleProvider);
      // Auth state listener will handle the response
    } catch (error) {
      console.error('Error signing in:', error);
      setLoading(false);
    }
  };

  // Show loading indicator while checking authentication status
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading authentication...</p>
      </div>
    );
  }

  return (
    <>
      {!isAuthorized ? (
        <div className="login-page">
            <div className="login-logo">
              <img 
                src="./logo_w_name.png" 
                alt="Logo" 
                className="app-logo"
              />
            </div>
            <h1>Student-Teacher Ratio Monitor</h1>
            <div className="login-card">
              <h2>Welcome</h2>
              <p>Please sign in with your Sunshine Starts account to access the application.</p>
              <p className="domain-info">@sunshinestarts.com</p>
              <button 
                className="firebase-login-button"
                onClick={handleSignIn}
              >
                Sign in with Google
              </button>
            </div>
        </div>
      ) : (
        <>
          {children}
        </>
      )}
    </>
  );
};

export default AuthGuard;